// แปลงตำแหน่งเป็นข้อความภาษาไทย (docs/PLAN.md ข้อ 4.3)

import type { LatLon } from "../engine/grid";
import { BIOME_TH, type BiomeId } from "./biomes";
import { neighbors } from "../engine/grid";
import { countryAt, hasSea, nearestCountry, seaNameAt, type GeoGrid } from "./geodata";
import { provinceAt, provinceLabel } from "./provinces";

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(["th"], { type: "region" });
  } catch {
    return null;
  }
})();

/** ชื่อประเทศภาษาไทยจากรหัส ISO เช่น TH → ไทย */
export function countryName(code: string): string {
  try {
    return regionNames?.of(code) ?? code;
  } catch {
    return code;
  }
}

/** จังหวัดที่พิกัดนี้อยู่ ใช้เฉพาะในไทยและเมื่อโหลดเส้นเขตจังหวัดได้ */
function thaiProvince(grid: GeoGrid, code: string | null, at: LatLon | undefined, maxDeg: number): string | null {
  if (code !== "TH" || !at || !grid.provinces) return null;
  const p = provinceAt(grid.provinces, at, maxDeg);
  return p && provinceLabel(p);
}

/** ประเทศเพื่อนบ้านในช่องรอบๆ (ช่องชายแดนนับเป็นของไทย แต่จุดจริงอยู่อีกฝั่ง เช่น เวียงจันทน์) */
function neighborCountry(grid: GeoGrid, cell: number): string | null {
  for (const c of neighbors(cell, 1)) {
    const code = countryAt(grid, c);
    if (code && code !== "TH") return code;
  }
  return null;
}

function landName(grid: GeoGrid, cell: number, at?: LatLon): string {
  const code = nearestCountry(grid, cell, 2);
  if (code === "TH" && at && grid.provinces) {
    // ชายฝั่งที่เส้นขอบลดจุดไปแล้วใช้จังหวัดที่ใกล้ที่สุด ชายแดนบนบกต้องอยู่ในจังหวัดจริงๆ
    const coastal = hasSea(grid, cell);
    const province = thaiProvince(grid, code, at, coastal ? 0.3 : 0.01); // 0.01° กันช่องว่างเล็กๆ ระหว่างจังหวัดที่ลดจุดแยกกัน
    if (province) return province;
    const other = coastal ? null : neighborCountry(grid, cell);
    if (other) return countryName(other);
  }
  return code ? countryName(code) : "แผ่นดินห่างไกล";
}

/**
 * ชื่อสถานที่ เช่น "อินเดีย" "อ่าวไทย นอกชายฝั่งเวียดนาม"
 * ในไทยบอกเป็นจังหวัดแทนชื่อประเทศ เช่น "จังหวัดเชียงใหม่" "อ่าวไทย นอกชายฝั่งจังหวัดชลบุรี" (ต้องส่งพิกัดมาด้วย)
 */
export function placeName(grid: GeoGrid, cell: number, onSea: boolean, at?: LatLon): string {
  if (onSea) {
    const sea = seaNameAt(grid, cell) ?? "ทะเล";
    const coast = nearestCountry(grid, cell, 1);
    const province = thaiProvince(grid, coast, at, 1);
    return province ? `${sea} นอกชายฝั่ง${province}` : coast ? `${sea} นอกชายฝั่ง${countryName(coast)}` : sea;
  }
  return landName(grid, cell, at);
}

/** ประเภทพื้นที่ · ชื่อสถานที่ เช่น "ป่าดิบชื้นเขตร้อน · ไทย" */
export function placeLabel(grid: GeoGrid, where: { cell: number; onSea: boolean; biome: BiomeId | null; at?: LatLon }): string {
  const place = placeName(grid, where.cell, where.onSea, where.at);
  return where.biome ? `${BIOME_TH[where.biome]} · ${place}` : place;
}

const fixed1 = (n: number) => Math.abs(n).toFixed(1);

/** พิกัดแบบ 16.8°N 98.9°E */
export function formatCoords({ lat, lon }: LatLon): string {
  return `${fixed1(lat)}°${lat >= 0 ? "N" : "S"} ${fixed1(lon)}°${lon >= 0 ? "E" : "W"}`;
}

function roundMeters(m: number): number {
  const a = Math.abs(m);
  return a < 100 ? Math.round(a) : a < 1000 ? Math.round(a / 10) * 10 : Math.round(a / 50) * 50;
}

const meters = (m: number) => `${roundMeters(m).toLocaleString("th-TH")} ม.`;

export interface Altitude {
  onSea: boolean;
  altitude: number; // ตำแหน่งของเราเทียบกับระดับน้ำทะเล (ติดลบ = ใต้ระดับน้ำทะเล)
  ground: number; // พื้นดินหรือพื้นทะเลตรงนั้น
}

/** ข้อความใต้แถบความสูง */
export function altitudeText({ onSea, altitude, ground }: Altitude): string {
  if (onSea) {
    const floor = `ทะเลแถบนี้ลึกราว ${meters(ground)}`;
    return altitude > -1 ? `อยู่ที่ผิวน้ำ · ${floor}` : `ลึกจากผิวน้ำราว ${meters(altitude)} · ${floor}`;
  }
  if (Math.abs(altitude) < 10) return "อยู่ราวระดับน้ำทะเล";
  return altitude > 0 ? `สูงจากระดับน้ำทะเลราว ${meters(altitude)}` : `ต่ำกว่าระดับน้ำทะเลราว ${meters(altitude)}`;
}
